import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, PlayCircle, RefreshCw, Camera } from "lucide-react";
import { api, API, useAuth } from "../lib/api";

const STAGE_LABELS = {
  starting: "Başlatılıyor...",
  capturing: "Ekran görüntüleri çekiliyor...",
  merging: "Birleştiriliyor...",
  done: "Tamamlandı",
};

export default function TourModal({ onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState({ exists: false, generating: false, progress: {} });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const startTick = () => {
    setElapsed(0);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  };
  const stopAll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/tour/status");
      setStatus(data);
      return data;
    } catch (e) {
      setError(e.response?.data?.detail || "Durum alınamadı");
      setPhase("error");
      return null;
    }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await refresh();
      if (s?.exists && !s.generating) {
        stopAll();
        setPhase("ready");
      }
    }, 4000);
  };

  const trigger = async (mode = "generate") => {
    setPhase("generating");
    startTick();
    try {
      const path = mode === "recapture" ? "/tour/recapture" : "/tour/generate";
      const params = mode === "generate-force" ? { force: 1 } : {};
      await api.post(path, null, { params });
      startPolling();
    } catch (e) {
      stopAll();
      setError(e.response?.data?.detail || "Üretim başlatılamadı");
      setPhase("error");
    }
  };

  useEffect(() => {
    (async () => {
      const s = await refresh();
      if (!s) return;
      if (s.exists && !s.generating) setPhase("ready");
      else if (s.generating) {
        setPhase("generating");
        startTick();
        startPolling();
      } else {
        await trigger("generate");
      }
    })();
    return stopAll;
    // eslint-disable-next-line
  }, []);

  const token = localStorage.getItem("tk_token") || "";
  const videoSrc = `${API}/tour/video?auth=${encodeURIComponent(token)}&v=${status.size}`;
  const stage = status.progress?.stage || "";
  const stageLabel = STAGE_LABELS[stage] || (stage.startsWith("scene_") ? `Sahne ${stage.split("_")[1]} işleniyor...` : "Hazırlanıyor...");

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-3" style={{ backgroundColor: "rgba(0,0,0,0.85)" }} data-testid="tour-modal">
      <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(255,255,255,0.18)", color: "white" }} data-testid="tour-close-btn" aria-label="Kapat">
          <X className="w-5 h-5" />
        </button>
        <div className="rounded-3xl overflow-hidden" style={{ aspectRatio: "9/16", backgroundColor: "#0B132B", boxShadow: "0 20px 60px rgba(0,0,0,0.5)", border: "3px solid rgba(255,255,255,0.15)" }}>
          {phase === "ready" && (
            <video key={status.size} src={videoSrc} controls autoPlay playsInline className="w-full h-full object-contain" data-testid="tour-video" />
          )}
          {phase === "loading" && (
            <div className="w-full h-full flex flex-col items-center justify-center text-white gap-3 px-6 text-center">
              <Loader2 className="w-8 h-8 animate-spin" />
              <div className="text-sm opacity-80">Tur kontrol ediliyor...</div>
            </div>
          )}
          {phase === "generating" && (
            <div className="w-full h-full flex flex-col items-center justify-center text-white gap-3 px-6 text-center">
              <PlayCircle className="w-12 h-12" style={{ color: "#ff6b35" }} />
              <div className="font-display font-semibold text-lg">Tur hazırlanıyor</div>
              <Loader2 className="w-6 h-6 animate-spin opacity-70" />
              <div className="text-sm opacity-90 font-medium" data-testid="tour-stage">{stageLabel}</div>
              <div className="text-xs opacity-60">
                Türkçe seslendirme + ekran gezintisi
                <br />Tahmini süre: 60-150 sn · {elapsed}s
              </div>
              <div className="text-xs opacity-50 mt-2">Sonraki açılışlarda anında başlayacak.</div>
            </div>
          )}
          {phase === "error" && (
            <div className="w-full h-full flex flex-col items-center justify-center text-white gap-3 px-6 text-center">
              <div className="text-sm">{error || "Bir hata oluştu"}</div>
              <button onClick={() => trigger("generate-force")} className="tk-btn flex items-center gap-1.5" data-testid="tour-retry-btn">
                <RefreshCw className="w-4 h-4" /> Tekrar dene
              </button>
            </div>
          )}
        </div>
        {phase === "ready" && isAdmin && (
          <div className="flex items-center justify-center gap-3 mt-3">
            <button onClick={() => trigger("generate-force")} className="text-xs flex items-center gap-1 opacity-70 hover:opacity-100" style={{ color: "white" }} data-testid="tour-regenerate-btn" title="Aynı görüntülerle yeniden seslendir">
              <RefreshCw className="w-3 h-3" /> Sadece sesi yenile
            </button>
            <span className="opacity-40" style={{ color: "white" }}>·</span>
            <button onClick={() => trigger("recapture")} className="text-xs flex items-center gap-1 opacity-70 hover:opacity-100" style={{ color: "white" }} data-testid="tour-recapture-btn" title="Ekranları yeniden çek + seslendir">
              <Camera className="w-3 h-3" /> Ekranları yenile
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
